import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import {
  setCurrentColumnKey,
  setIdNameMap,
  setUniqueValues,
} from 'redux/slice/filter/Filter.slice';
import {
  IGetAgentListApiResponse,
  IGetAgentListRequest,
  IGetAgentListResponse,
  IGetAgentsCountResponse,
  IGetUniqueValuesByColumnApiResponse,
  IGetUniqueValuesByColumnRequest,
  ISearchAgentListRequest,
} from 'redux/types/Agent.types';
import {
  agentEndpoints,
  agentListEndpoints,
  userEndpoints,
} from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import {
  fetchAgentRecordsByAgencyId,
  fetchAgentRecordsByAgencyIdFailure,
  fetchAgentRecordsByAgencyIdSuccess,
  getAgentsCount,
  getAgentsCountFailure,
  getAgentsCountSuccess,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameFailure,
  getUniqueValuesByColumnNameSuccess,
  offboardAgent,
  offboardAgentFailure,
  offboardAgentSuccess,
  searchAgentRecords,
  searchAgentRecordsFailure,
  searchAgentRecordsSuccess,
  setAgentListRecordIds,
  unblockUser,
  unblockUserFailure,
  unblockUserSuccess,
} from '../../slice/agent/AgentList.slice';
import { handleError } from '../utils/errorUtils';

// Saga for fetching agent records for data grid
function* fetchAgentRecordsSaga(action: PayloadAction<IGetAgentListRequest>) {
  try {
    const {
      agentType,
      limit = 10,
      offset = 0,
      columns,
      ungroupedAgents = 'false',
    } = action.payload;

    const queryParams = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    if (agentType === 'callAgents') {
      queryParams.append('channel', 'CALL');
      queryParams.append('ungrouped', ungroupedAgents);
    } else if (agentType === 'fieldAgents') {
      queryParams.append('channel', 'FIELD');
    }

    const endpoint = agentListEndpoints.GET_AGENT_DETAILS_LIST();
    const payload = {
      columns: columns.length === 0 ? [] : columns,
    };

    const response: AxiosResponse<IGetAgentListApiResponse> =
      yield HTTPClient.PostRequest(
        `${endpoint}?${queryParams.toString()}`,
        payload
      );

    const responseData: IGetAgentListResponse = {
      data: response.data.data.agents,
      totalRecords: response.data.data.totalRecords,
      loading: false,
      error: null,
      uniqueValues: [],
      filteredValues: [],
      bucket: null,
      channel: null,
      pagination: {
        limit: response.data.data.limit,
        offset: response.data.data.offset,
        page: Math.floor(response.data.data.offset / response.data.data.limit),
        pageSize: response.data.data.limit,
      },
      agent_id: null,
      agent_name: null,
      invalid_records: 0,
      no_follow_up_record: 0,
    };
    yield put(fetchAgentRecordsByAgencyIdSuccess(responseData));
  } catch (error: any) {
    yield* handleError(error, fetchAgentRecordsByAgencyIdFailure, '');
  }
}

// Saga for searching agent records
function* searchAgentRecordsSaga(
  action: PayloadAction<ISearchAgentListRequest>
) {
  try {
    const {
      limit = 10,
      offset = 0,
      columns,
      searchTerm,
      channel,
    } = action.payload;

    if (!searchTerm) {
      yield put(
        searchAgentRecordsFailure({
          error: { message: 'Search term is required' },
        })
      );
      return;
    }

    const queryParams = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      query: searchTerm,
      channel: channel ?? '',
    });

    const endpoint = agentListEndpoints.GET_AGENT_DETAILS_LIST_SEARCH();
    const payload = {
      columns: columns.length === 0 ? [] : columns,
    };

    const response: AxiosResponse<IGetAgentListApiResponse> =
      yield HTTPClient.PostRequest(
        `${endpoint}?${queryParams.toString()}`,
        payload
      );

    const responseData: IGetAgentListResponse = {
      data: response.data.data.agents,
      totalRecords: response.data.data.totalRecords,
      loading: false,
      error: null,
      uniqueValues: [],
      filteredValues: [],
      bucket: null,
      channel: null,
      pagination: {
        limit: response.data.data.limit,
        offset: response.data.data.offset,
        page: Math.floor(response.data.data.offset / response.data.data.limit),
        pageSize: response.data.data.limit,
      },
      agent_id: null,
      agent_name: null,
      invalid_records: 0,
      no_follow_up_record: 0,
    };
    yield put(searchAgentRecordsSuccess(responseData));
  } catch (error: any) {
    yield* handleError(error, searchAgentRecordsFailure, '');
  }
}

// Saga for getting unique values by column name
function* getUniqueValuesByColumnNameSaga(
  action: PayloadAction<IGetUniqueValuesByColumnRequest>
) {
  try {
    const { field, channel } = action.payload;
    const response: AxiosResponse<IGetUniqueValuesByColumnApiResponse> =
      yield HTTPClient.PostRequest(
        `${agentListEndpoints.GET_AGENT_DETAILS_LIST_DISTINCT(
          field,
          channel ?? ''
        )}`,
        { columns: [] }
      );

    const { data } = response.data;
    let uniqueValues: string[] = [];

    // Set current column key first so idNameMap can be stored correctly
    yield put(setCurrentColumnKey(field));

    // Check if response has idNameMap (for DRA, agency, supervisor)
    if (data.idNameMap) {
      // Store the mapping and use names as display values
      yield put(setIdNameMap(data.idNameMap));
      uniqueValues = Object.values(data.idNameMap);
    } else if (data.values) {
      // For other columns, use values array directly
      uniqueValues = data.values;
      yield put(setIdNameMap({}));
    }

    yield put(getUniqueValuesByColumnNameSuccess(uniqueValues));
    yield put(setUniqueValues(uniqueValues));

    if (field === 'id') {
      yield put(setAgentListRecordIds(uniqueValues));
    }
    // message.success('Unique values fetched successfully');
  } catch (error: any) {
    yield* handleError(error, getUniqueValuesByColumnNameFailure, '');
  }
}

// Saga for getting agents count
function* getAgentsCountSaga() {
  try {
    const response: AxiosResponse<IGetAgentsCountResponse> =
      yield HTTPClient.GetRequest(
        agentListEndpoints.GET_AGENT_DETAILS_LIST_COUNT()
      );
    yield put(getAgentsCountSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getAgentsCountFailure, '');
  }
}
// Saga for offboarding agents
function* offboardAgentSaga(action: PayloadAction<{ agentId: string }>) {
  const { agentId } = action.payload;
  try {
    const response: AxiosResponse<{ message: string; status: string }> =
      yield HTTPClient.DeleteRequest(agentEndpoints.OFFBOARD_AGENT(agentId));
    yield put(offboardAgentSuccess());
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, offboardAgentFailure, '');
  }
}

function* unblockUserSaga(
  action: PayloadAction<{ userId: string; agentName: string }>
) {
  const { userId } = action.payload;
  try {
    yield HTTPClient.PostRequest(userEndpoints.UNBLOCK_USER(userId));
    yield put(unblockUserSuccess());
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.error?.message || 'Failed to unblock user';
    yield put(unblockUserFailure({ error: {}, errorMsg }));
  }
}

export function* watchAgentListSagas() {
  yield takeLatest(fetchAgentRecordsByAgencyId.type, fetchAgentRecordsSaga);
  yield takeLatest(searchAgentRecords.type, searchAgentRecordsSaga);
  yield takeLatest(
    getUniqueValuesByColumnName.type,
    getUniqueValuesByColumnNameSaga
  );
  yield takeLatest(getAgentsCount.type, getAgentsCountSaga);
  yield takeLatest(offboardAgent.type, offboardAgentSaga);
  yield takeLatest(unblockUser.type, unblockUserSaga);
}
